import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Users, Shield, MapPin, Camera, QrCode, FileText,
  Mic, MicOff, Trash2, RefreshCw, X, CheckCircle2, ChevronRight,
  Compass, ImagePlus, Save,
} from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import { api } from "@/lib/api";
import { TopBar } from "@/components/layout/TopBar";
import { PageShell, PageHeader } from "@/components/altiora/PremiumUI";
import { useNavigate } from "react-router-dom";

const NAVY = "#0B2545";
const NAVY_DEEP = "#081A33";
const CHAMPAGNE = "#C5A880";
const GOLD = "#F5D687";
const GREEN = "#0B7A3B";
const CRIMSON = "#B91C1C";
const CREAM = "#FBF6EA";

/* ══════════════════════════════════════════════════════════════════
   MEMBERS OPT-IN — Granular consent form
   ══════════════════════════════════════════════════════════════════ */
function ConsentPanel() {
  const [opts, setOpts] = useState({ pro_types: [], sectors: [], regions: [] });
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [o, cs] = await Promise.all([
        api.get("/intel/members/options"),
        api.get("/intel/members/consent"),
      ]);
      setOpts(o.data); setC(cs.data);
    } catch { toast.error("Erreur de chargement."); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const toggle = (key, val) => {
    setC(x => {
      const arr = new Set(x[key] || []);
      arr.has(val) ? arr.delete(val) : arr.add(val);
      return { ...x, [key]: Array.from(arr) };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/intel/members/consent", {
        visible_in_network: c.visible_in_network,
        allow_intro:        c.allow_intro,
        allowed_pro_types:  c.allowed_pro_types,
        allowed_sectors:    c.allowed_sectors,
        allowed_regions:    c.allowed_regions,
      });
      setC(r.data);
      toast.success("Préférences enregistrées");
    } catch { toast.error("Impossible d'enregistrer."); }
    setSaving(false);
  };

  const revoke = async () => {
    if (!window.confirm("Retirer tout consentement ?")) return;
    try {
      await api.delete("/intel/members/consent");
      toast.success("Consentement révoqué");
      await load();
    } catch { toast.error("Impossible."); }
  };

  if (loading || !c) return (
    <div className="p-4"><Loader2 size={16} className="animate-spin" /></div>
  );

  const chip = (arr, val, group) => {
    const on = (c[group] || []).includes(val);
    return (
      <button key={val} onClick={() => toggle(group, val)}
              data-testid={`consent-${group}-${val}`}
              className="text-[11px] px-2.5 py-1 rounded-full transition"
              style={{ background: on ? NAVY : "#F8FAFC",
                         color: on ? "#fff" : NAVY,
                         border: `1px solid ${on ? NAVY : CHAMPAGNE + "55"}`,
                         fontWeight: on ? 600 : 500 }}>
        {val}
      </button>
    );
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm p-6" data-testid="consent-panel">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
             style={{ background: CREAM }}>
          <Shield size={18} style={{ color: NAVY }} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest" style={{ color: CHAMPAGNE }}>
            Consentement · Réseau ALTIORA
          </div>
          <h3 className="font-serif text-xl leading-tight" style={{ color: NAVY }}>
            Vos choix, votre visibilité
          </h3>
          <p className="text-[12px] text-slate-500 mt-1 max-w-xl">
            Choisissez précisément qui peut vous voir dans l'annuaire ALTIORA et
            avec quels professionnels vous acceptez d'être mis en relation. Vous
            pouvez ajuster ou retirer votre consentement à tout moment.
          </p>
        </div>
      </div>

      {/* Master toggles */}
      <div className="space-y-3 mb-6">
        <ToggleRow
          testid="consent-visible"
          label="Visible dans l'annuaire ALTIORA"
          hint="Les autres membres consentants peuvent vous découvrir."
          value={c.visible_in_network}
          onChange={v => setC(x => ({ ...x, visible_in_network: v,
                                            allow_intro: v && x.allow_intro }))} />
        <ToggleRow
          testid="consent-intro"
          label="Accepter les mises en relation"
          hint="Uniquement si votre profil est visible."
          value={c.allow_intro} disabled={!c.visible_in_network}
          onChange={v => setC(x => ({ ...x, allow_intro: v }))} />
      </div>

      {/* Filters */}
      <div className={c.visible_in_network ? "" : "opacity-50 pointer-events-none"}>
        <Section label="Types de professionnels autorisés">
          <div className="flex flex-wrap gap-1.5" data-testid="consent-pro-types">
            {opts.pro_types.map(p => chip(opts.pro_types, p, "allowed_pro_types"))}
          </div>
        </Section>
        <Section label="Secteurs de services autorisés">
          <div className="flex flex-wrap gap-1.5" data-testid="consent-sectors">
            {opts.sectors.map(s => chip(opts.sectors, s, "allowed_sectors"))}
          </div>
        </Section>
        <Section label="Zones géographiques autorisées">
          <div className="flex flex-wrap gap-1.5" data-testid="consent-regions">
            {opts.regions.map(r => chip(opts.regions, r, "allowed_regions"))}
          </div>
        </Section>
        <p className="text-[10px] text-slate-500 mt-2 italic">
          Aucune sélection = accepter tous. Le principe de réciprocité s'applique dans les deux sens.
        </p>
      </div>

      <div className="flex gap-2 mt-6 pt-4 border-t">
        <button onClick={save} disabled={saving} data-testid="consent-save-btn"
                className="text-[12px] px-4 py-2 rounded-lg flex items-center gap-1.5"
                style={{ background: NAVY, color: "#fff", fontWeight: 600 }}>
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
          Enregistrer mes préférences
        </button>
        <button onClick={revoke} data-testid="consent-revoke-btn"
                className="text-[12px] px-3 py-2 rounded-lg"
                style={{ background: "#FEF2F2", color: CRIMSON }}>
          Révoquer tout
        </button>
        {c.revoked_at && (
          <span className="text-[10px] text-slate-500 italic self-center">
            Révoqué le {new Date(c.revoked_at).toLocaleString("fr-CA")}
          </span>
        )}
      </div>
    </div>
  );
}

function ToggleRow({ label, hint, value, onChange, disabled, testid }) {
  return (
    <div className={`flex items-start justify-between p-3 rounded-lg ${disabled ? "opacity-50" : ""}`}
         style={{ background: value ? "#FBF6EA" : "#F8FAFC" }}>
      <div className="flex-1 pr-3">
        <div className="text-[13px] font-semibold" style={{ color: NAVY }}>{label}</div>
        <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>
      </div>
      <button onClick={() => !disabled && onChange(!value)}
              data-testid={testid}
              disabled={disabled}
              aria-disabled={disabled ? "true" : "false"}
              className="w-11 h-6 rounded-full relative transition shrink-0"
              style={{ background: value ? GREEN : "#CBD5E1",
                         cursor: disabled ? "not-allowed" : "pointer" }}>
        <span className="absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition"
              style={{ left: value ? "22px" : "2px" }} />
      </button>
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div className="mt-4">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">{label}</div>
      {children}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   DIRECTORY — Members you can connect with
   ══════════════════════════════════════════════════════════════════ */
function DirectoryPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/intel/members/directory")
       .then(r => setData(r.data))
       .catch(() => toast.error("Erreur annuaire."))
       .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-4"><Loader2 size={16} className="animate-spin" /></div>;

  return (
    <div className="bg-white rounded-2xl shadow-sm p-5" data-testid="directory-panel">
      <div className="flex items-center gap-2 mb-3">
        <Users size={16} style={{ color: CHAMPAGNE }} />
        <h3 className="font-serif text-lg" style={{ color: NAVY }}>Annuaire du réseau</h3>
        <span className="text-[11px] text-slate-500 ml-auto">{data.count} membre(s)</span>
      </div>
      {data.hint_fr ? (
        <div className="text-[12px] p-3 rounded" style={{ background: CREAM, color: NAVY }}>
          {data.hint_fr}
        </div>
      ) : data.members.length === 0 ? (
        <div className="text-[12px] text-slate-500 italic">
          Aucun membre correspondant à vos filtres pour l'instant.
        </div>
      ) : (
        <div className="space-y-2" data-testid="directory-members-list">
          {data.members.map((m, i) => (
            <div key={m.user_id} className="flex items-center gap-3 p-3 rounded"
                 style={{ background: "#F8FAFC" }}
                 data-testid={`directory-member-${i}`}>
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-[13px]"
                   style={{ background: CHAMPAGNE }}>
                {(m.name || "?").charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold" style={{ color: NAVY }}>{m.name}</div>
                <div className="text-[11px] text-slate-500 flex flex-wrap gap-1">
                  {[m.pro_type, m.sector, m.city].filter(Boolean).join(" · ")}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   FIELD SCAN KIT — QR + Photo + Geo + Notes + Voice
   ══════════════════════════════════════════════════════════════════ */
function FieldScanPanel() {
  const [qr, setQr] = useState("");
  const [scanning, setScanning] = useState(false);
  const [cardImg, setCardImg] = useState(null);
  const [contextImg, setContextImg] = useState(null);
  const [voiceBlob, setVoiceBlob] = useState(null);
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [notes, setNotes] = useState("");
  const [geo, setGeo] = useState(null);
  const [placeName, setPlaceName] = useState("");
  const [placeAddress, setPlaceAddress] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const cardRef = useRef(null);
  const ctxRef = useRef(null);
  const scannerRef = useRef(null);
  const mediaRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const chunksRef = useRef([]);

  const getGeo = () => {
    if (!navigator.geolocation) { toast.error("Géolocalisation indisponible."); return; }
    toast.info("Récupération de la position…");
    navigator.geolocation.getCurrentPosition(
      p => setGeo({ lat: p.coords.latitude, lng: p.coords.longitude, acc: p.coords.accuracy }),
      e => toast.error("Position refusée : " + e.message),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const startQr = async () => {
    setScanning(true); setQr("");
    try {
      const el = "qr-scanner-view";
      if (!scannerRef.current) scannerRef.current = new Html5Qrcode(el);
      await scannerRef.current.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: 240 },
        (text) => {
          setQr(text); toast.success("QR détecté");
          scannerRef.current.stop().then(() => scannerRef.current.clear())
                          .catch(() => {});
          setScanning(false);
        },
        () => {},
      );
    } catch (e) {
      setScanning(false);
      toast.error("Caméra refusée : " + (e?.message || ""));
    }
  };
  const stopQr = async () => {
    try { await scannerRef.current?.stop(); await scannerRef.current?.clear(); } catch {}
    setScanning(false);
  };

  const startVoice = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = s;
      const mr = new MediaRecorder(s);
      chunksRef.current = [];
      mr.ondataavailable = e => chunksRef.current.push(e.data);
      mr.onstop = () => {
        setVoiceBlob(new Blob(chunksRef.current, { type: "audio/webm" }));
        s.getTracks().forEach(t => t.stop());
      };
      mr.start(); mediaRef.current = mr;
      setSeconds(0); setRecording(true);
      timerRef.current = setInterval(() => setSeconds(x => x + 1), 1000);
    } catch { toast.error("Micro refusé."); }
  };
  const stopVoice = () => {
    mediaRef.current?.stop();
    clearInterval(timerRef.current); timerRef.current = null;
    setRecording(false);
  };

  const submit = async () => {
    setSubmitting(true); setLastResult(null);
    const fd = new FormData();
    if (notes)         fd.append("notes_fr", notes);
    if (qr)            fd.append("qr_payload", qr);
    if (geo)          { fd.append("latitude", geo.lat); fd.append("longitude", geo.lng);
                          fd.append("accuracy_m", geo.acc || 0); }
    if (placeName)     fd.append("place_name", placeName);
    if (placeAddress)  fd.append("place_address", placeAddress);
    if (cardImg)       fd.append("card_image", cardImg, cardImg.name || "card.jpg");
    if (contextImg)    fd.append("context_image", contextImg, contextImg.name || "context.jpg");
    if (voiceBlob)     fd.append("voice_note", voiceBlob, "field-voice.webm");

    try {
      const r = await api.post("/intel/field/visit", fd,
                                    { headers: { "Content-Type": "multipart/form-data" } });
      setLastResult(r.data);
      toast.success(r.data.status === "auto_integrated"
        ? "Visite intégrée automatiquement"
        : "Visite en attente de validation dans la boîte");
      // Reset
      setQr(""); setCardImg(null); setContextImg(null); setVoiceBlob(null);
      setNotes(""); setPlaceName(""); setPlaceAddress(""); setGeo(null); setSeconds(0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'envoyer.");
    }
    setSubmitting(false);
  };

  const canSubmit = !submitting && (qr || cardImg || contextImg || voiceBlob || notes);

  return (
    <div className="bg-white rounded-2xl shadow-sm p-5" data-testid="field-scan-panel">
      <div className="flex items-center gap-2 mb-4">
        <Compass size={16} style={{ color: CHAMPAGNE }} />
        <h3 className="font-serif text-lg" style={{ color: NAVY }}>Kit de prospection terrain</h3>
      </div>

      {/* QR */}
      <Section label="Scanner un QR code / badge">
        {!scanning ? (
          <button onClick={startQr} data-testid="field-qr-start-btn"
                  className="text-[12px] px-3 py-2 rounded-lg flex items-center gap-1.5"
                  style={{ background: NAVY, color: "#fff", fontWeight: 600 }}>
            <QrCode size={13} /> Ouvrir le scanner
          </button>
        ) : (
          <>
            <div id="qr-scanner-view" className="rounded-lg overflow-hidden"
                 style={{ maxWidth: 300 }} data-testid="field-qr-scanner-view" />
            <button onClick={stopQr} className="text-[11px] mt-2 underline text-slate-500">
              Arrêter le scan
            </button>
          </>
        )}
        {qr && (
          <div className="mt-2 text-[12px] p-2 rounded" style={{ background: CREAM, color: NAVY }}
               data-testid="field-qr-payload">
            <b>QR :</b> {qr.slice(0, 200)}
          </div>
        )}
      </Section>

      {/* Photos */}
      <div className="grid grid-cols-2 gap-3 mt-4">
        <PhotoPicker label="Carte d'affaires" testid="field-card-picker"
                        ref={cardRef} file={cardImg} onFile={setCardImg} />
        <PhotoPicker label="Photo terrain / bâtiment" testid="field-context-picker"
                        ref={ctxRef} file={contextImg} onFile={setContextImg} />
      </div>

      {/* Geo */}
      <Section label="Localisation">
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={getGeo} data-testid="field-geo-btn"
                  className="text-[12px] px-3 py-1.5 rounded-lg flex items-center gap-1.5"
                  style={{ background: CREAM, color: NAVY, border: `1px solid ${CHAMPAGNE}55` }}>
            <MapPin size={13} /> {geo ? "Position obtenue" : "Obtenir ma position"}
          </button>
          {geo && (
            <span className="text-[11px] text-slate-600" data-testid="field-geo-value">
              {geo.lat.toFixed(5)}, {geo.lng.toFixed(5)} (±{Math.round(geo.acc || 0)} m)
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
          <input value={placeName} onChange={e => setPlaceName(e.target.value)}
                 placeholder="Nom du lieu (ex. Café Olimpico)"
                 data-testid="field-place-name-input"
                 className="text-[12px] p-2 rounded border" style={{ borderColor: CHAMPAGNE + "55" }} />
          <input value={placeAddress} onChange={e => setPlaceAddress(e.target.value)}
                 placeholder="Adresse / repère"
                 data-testid="field-place-address-input"
                 className="text-[12px] p-2 rounded border" style={{ borderColor: CHAMPAGNE + "55" }} />
        </div>
      </Section>

      {/* Voice */}
      <Section label="Mémo vocal">
        <div className="flex items-center gap-2">
          {!recording ? (
            <button onClick={startVoice} data-testid="field-voice-start-btn"
                    className="w-11 h-11 rounded-full flex items-center justify-center"
                    style={{ background: NAVY, color: "#fff" }}>
              <Mic size={16} />
            </button>
          ) : (
            <button onClick={stopVoice} data-testid="field-voice-stop-btn"
                    className="w-11 h-11 rounded-full flex items-center justify-center"
                    style={{ background: CRIMSON, color: "#fff" }}>
              <MicOff size={16} />
            </button>
          )}
          <span className="text-[12px]">
            {recording ? `● ${Math.floor(seconds/60)}:${String(seconds%60).padStart(2,"0")}`
                          : voiceBlob ? "Mémo enregistré ✓" : "Cliquez pour enregistrer"}
          </span>
          {voiceBlob && (
            <button onClick={() => setVoiceBlob(null)}
                    className="text-[11px] text-slate-500 underline ml-auto">
              Supprimer
            </button>
          )}
        </div>
      </Section>

      {/* Notes */}
      <Section label="Notes libres">
        <textarea value={notes} onChange={e => setNotes(e.target.value)}
                    rows={3} placeholder="Contexte de la rencontre, besoin exprimé, suivi souhaité…"
                    data-testid="field-notes-input"
                    className="w-full text-[12px] p-2 rounded border resize-none"
                    style={{ borderColor: CHAMPAGNE + "55" }} />
      </Section>

      {/* Submit */}
      <div className="flex items-center gap-2 mt-4 pt-4 border-t">
        <button onClick={submit} disabled={!canSubmit}
                data-testid="field-submit-btn"
                className="text-[13px] px-5 py-2.5 rounded-lg flex items-center gap-1.5 disabled:opacity-50"
                style={{ background: GOLD, color: NAVY, fontWeight: 700 }}>
          {submitting ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
          Envoyer la visite terrain
        </button>
        <span className="text-[10px] text-slate-500">
          ALTIORA analyse chaque signal via l'IA + pipeline universel.
        </span>
      </div>

      {lastResult && (
        <div className="mt-4 p-3 rounded" style={{ background: CREAM, border: `1px solid ${CHAMPAGNE}55` }}
             data-testid="field-last-result">
          <div className="text-[10px] uppercase tracking-widest" style={{ color: CHAMPAGNE }}>
            Dernière visite envoyée
          </div>
          <div className="text-[12px] mt-1">
            <b>{lastResult.contacts_processed?.length || 0}</b> contact(s) ·
            confiance <b>{(lastResult.confidence * 100).toFixed(0)} %</b> · statut{" "}
            <b>{lastResult.status}</b>
          </div>
        </div>
      )}
    </div>
  );
}

const PhotoPicker = (() => {
  const P = ({ label, file, onFile, testid }) => {
    const ref = useRef(null);
    return (
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</div>
        <button onClick={() => ref.current?.click()}
                data-testid={`${testid}-btn`}
                className="w-full h-24 rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-1 transition"
                style={{ borderColor: file ? GREEN : CHAMPAGNE + "88",
                            background: file ? GREEN + "1A" : "#FAFAFA", color: NAVY }}>
          {file ? (
            <>
              <CheckCircle2 size={16} style={{ color: GREEN }} />
              <span className="text-[10px] text-slate-600 truncate max-w-[140px]">{file.name}</span>
            </>
          ) : (
            <>
              <ImagePlus size={16} style={{ color: NAVY }} />
              <span className="text-[10px]">Choisir / photographier</span>
            </>
          )}
        </button>
        <input ref={ref} type="file" accept="image/*" capture="environment" className="hidden"
                 onChange={e => onFile(e.target.files?.[0] || null)}
                 data-testid={`${testid}-input`} />
      </div>
    );
  };
  return P;
})();

/* ══════════════════════════════════════════════════════════════════
   MAIN PAGE
   ══════════════════════════════════════════════════════════════════ */
export default function NetworkAndScan() {
  const nav = useNavigate();
  const [tab, setTab] = useState("network"); // network | field

  return (
    <PageShell testid="network-scan-root">
      <TopBar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-6 pb-16">
        <PageHeader
          eyebrow="Réseau ALTIORA · Terrain"
          title="Consentement, annuaire et prospection terrain"
          subtitle="Choisissez précisément votre visibilité dans le réseau et transformez chaque visite terrain en opportunité qualifiée."
          onBack={() => nav("/app/intel-commerciale")}
          testid="ns-header" />

        <div className="flex gap-1 p-1 rounded-lg bg-white shadow-sm mb-5 max-w-md"
             style={{ border: `1px solid ${CHAMPAGNE}22` }}>
          {[
            { id: "network", label: "Réseau ALTIORA", icon: Users },
            { id: "field",   label: "Kit terrain",    icon: Compass },
          ].map(t => {
            const T = t.icon; const on = tab === t.id;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                      data-testid={`ns-tab-${t.id}`}
                      className="flex-1 text-[12px] py-2 rounded-md flex items-center justify-center gap-1.5"
                      style={{ background: on ? NAVY : "transparent",
                                 color:      on ? "#fff" : NAVY,
                                 fontWeight: on ? 600 : 500 }}>
                <T size={13} /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "network" ? (
          <div className="space-y-5">
            <ConsentPanel />
            <DirectoryPanel />
          </div>
        ) : (
          <FieldScanPanel />
        )}
      </div>
    </PageShell>
  );
}
